import { cn } from "@design-system/utils";
import { ChevronDownIcon, XIcon } from "lucide-react";
import * as React from "react";

import { Badge } from "../badge";
import { Button } from "../button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

interface FieldTriggerProps {
  children: React.ReactNode;
  hasValue?: boolean;
  disabled?: boolean;
  onClear?: () => void;
  className?: string;
}

const FieldTriggerContext = React.createContext<{ showClear: boolean }>({
  showClear: false,
});

function FieldTrigger({
  children,
  hasValue,
  disabled,
  onClear,
  className,
}: FieldTriggerProps) {
  const showClear = !!hasValue && !!onClear && !disabled;
  const contextValue = React.useMemo(() => ({ showClear }), [showClear]);

  return (
    <FieldTriggerContext.Provider value={contextValue}>
      <div
        data-slot="field-trigger"
        className={cn("relative block", className)}
      >
        {children}
        <div
          className={cn(
            "pointer-events-none absolute top-1/2 right-3 z-10 flex -translate-y-1/2 items-center gap-2",
            disabled && "opacity-50"
          )}
        >
          {showClear && (
            <Button
              type="button"
              variant="muted"
              size="unstyled"
              aria-label="Clear selection"
              className="pointer-events-auto size-5 p-0 hover:bg-transparent"
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <XIcon className="text-shape-neutral-ghost-foreground-muted size-5" />
            </Button>
          )}
          <ChevronDownIcon className="text-shape-neutral-outlined-foreground-default size-5 shrink-0" />
        </div>
      </div>
    </FieldTriggerContext.Provider>
  );
}

interface FieldTriggerButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  multiLine?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

function FieldTriggerButton({
  children,
  className,
  multiLine = false,
  onPointerDown,
  onKeyDown,
  onClick,
  ref,
  ...props
}: FieldTriggerButtonProps) {
  const hadPointerDownRef = React.useRef(false);
  const hadKeyDownRef = React.useRef(false);
  const { showClear } = React.useContext(FieldTriggerContext);

  return (
    <button
      ref={ref}
      type="button"
      data-slot="field-trigger-button"
      className={cn(
        "border-shape-neutral-outlined-border-emphasised bg-shape-neutral-outlined-background text-shape-neutral-outlined-foreground-default data-[placeholder]:text-shape-neutral-outlined-foreground-muted focus-visible:ring-ring-default focus:ring-ring-default aria-invalid:border-shape-critical-outlined-border-emphasised flex w-full cursor-pointer items-center gap-2 self-stretch rounded-lg border px-3 py-2 text-left text-sm leading-normal font-normal transition-[color,box-shadow] outline-none focus:ring-[3px] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        showClear ? "pr-16" : "pr-10",
        multiLine ? "min-h-9 items-start" : "h-9",
        className
      )}
      onPointerDown={(event) => {
        hadPointerDownRef.current = true;
        onPointerDown?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          hadKeyDownRef.current = true;
        }
        onKeyDown?.(event);
      }}
      onClick={(event) => {
        // When a <label htmlFor> is clicked, the browser dispatches a click on
        // the associated element but NOT a pointerdown or keydown. Per HTML spec,
        // clicking a label for a native <select> focuses it without opening the
        // dropdown. Match that behavior: focus the trigger and prevent opening.
        if (!hadPointerDownRef.current && !hadKeyDownRef.current) {
          event.currentTarget.focus();
          event.preventDefault();
        }
        hadPointerDownRef.current = false;
        hadKeyDownRef.current = false;
        onClick?.(event);
      }}
      {...props}
    >
      <span
        data-slot="field-trigger-content"
        className={cn(
          "flex flex-1 items-center gap-2",
          multiLine ? "flex-wrap" : "overflow-hidden whitespace-nowrap"
        )}
      >
        {children}
      </span>
    </button>
  );
}

interface FieldTriggerValuesProps {
  children: React.ReactNode;
  labels?: string[];
  countSuffix?: string;
  className?: string;
}

function ValuesTooltip({
  labels,
  children,
}: {
  labels?: string[];
  children: React.ReactNode;
}) {
  if (!labels || labels.length === 0) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-96">{labels.join(", ")}</TooltipContent>
    </Tooltip>
  );
}

const GAP_PX = 8; // Tailwind gap-2

function FieldTriggerValues({
  children,
  labels,
  countSuffix,
  className,
}: FieldTriggerValuesProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState<number | null>(null);

  const childArray = React.Children.toArray(children);
  const totalCount = childArray.length;

  const calculate = React.useCallback(() => {
    const container = containerRef.current;
    const measureContainer = measureRef.current;
    if (!container || !measureContainer) return;

    const containerWidth = container.clientWidth;
    if (containerWidth === 0) return;

    const measureChildren = Array.from(
      measureContainer.children
    ) as HTMLElement[];

    // Last child in the measurement container is the overflow badge
    const items = measureChildren.slice(0, -1);
    const overflowBadge = measureChildren.at(-1);

    if (items.length === 0) {
      setVisibleCount(0);
      return;
    }

    // Check if all items fit without an overflow badge
    let totalWidth = 0;
    for (let i = 0; i < items.length; i++) {
      totalWidth += items[i].offsetWidth + (i > 0 ? GAP_PX : 0);
    }

    if (totalWidth <= containerWidth) {
      setVisibleCount(items.length);
      return;
    }

    // Not all fit — find how many fit alongside the overflow badge
    const badgeWidth = overflowBadge?.offsetWidth ?? 0;
    const availableForItems = containerWidth - GAP_PX - badgeWidth;
    let usedWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < items.length; i++) {
      const gapBefore = fitCount > 0 ? GAP_PX : 0;
      const widthAfterAdding = usedWidth + gapBefore + items[i].offsetWidth;

      if (widthAfterAdding <= availableForItems) {
        usedWidth = widthAfterAdding;
        fitCount++;
      } else {
        break;
      }
    }

    setVisibleCount(fitCount);
  }, []);

  // Synchronous measurement before paint — runs on mount and when children count changes
  React.useLayoutEffect(() => {
    calculate();
  }, [childArray.length, calculate]);

  // Recalculate on container resize or when measurement content changes dimensions
  React.useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const observer = new ResizeObserver(() => calculate());
    observer.observe(container);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [calculate]);

  if (totalCount === 0) return null;

  const effectiveVisible = Math.min(visibleCount ?? 0, totalCount);
  const hiddenCount = totalCount - effectiveVisible;
  const showCountOnly = effectiveVisible === 0;

  return (
    <>
      {/* Off-screen container that always renders all items for width measurement */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none fixed flex items-center gap-2"
        style={{ visibility: "hidden", top: -9999, left: -9999 }}
      >
        {children}
        <Badge semantic="neutral" emphasis="muted">
          +{totalCount}
        </Badge>
      </div>
      <div
        ref={containerRef}
        data-slot="field-trigger-values"
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2 overflow-hidden",
          visibleCount === null && "invisible",
          className
        )}
      >
        {showCountOnly ? (
          <ValuesTooltip labels={labels}>
            <Badge semantic="neutral" emphasis="muted">
              {totalCount}
              {countSuffix ? ` ${countSuffix}` : null}
            </Badge>
          </ValuesTooltip>
        ) : (
          <>
            {childArray.slice(0, effectiveVisible)}
            {hiddenCount > 0 ? (
              <ValuesTooltip labels={labels?.slice(effectiveVisible)}>
                <Badge semantic="neutral" emphasis="muted">
                  +{hiddenCount}
                </Badge>
              </ValuesTooltip>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

export { FieldTrigger, FieldTriggerButton, FieldTriggerValues };
export type {
  FieldTriggerProps,
  FieldTriggerButtonProps,
  FieldTriggerValuesProps,
};
